import { useState, type ReactNode } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import {
  INTELLIGENCE_LEVELS,
  type PlanVersion,
  type PlanVersionContent,
  type PriceComponent,
  type TokenEstimate,
} from '@traceio-org/platform-contracts';
import { PageHeading, PageSection } from '@traceio-org/ui';
import { estimateAtLevel } from '../../billing/estimates';
import {
  formatMoney,
  formatMultiplier,
  formatPercent,
  formatTokens,
  formatUnitPrice,
} from '../../billing/money';
import { AdminPage } from '../../components/AdminPage';
import { ActionLink, AdminProblem } from '../../components/AdminProblem';
import { LoadingBlock, SavedNotice } from '../../components/feedback';
import { ReasonField, reasonError } from '../../components/fields';
import { DateTimeText, ScrollTable, StatusBadge } from '../../components/records';
import type { WriteFailure } from '../../api';
import { WriteDialog, useDialogTarget } from '../../components/WriteDialog';
import { useAbilities, useAdmin } from '../../context';
import {
  DISCOUNT_PRORATION_LABELS,
  LICENSED_UNIT_LABELS,
  PLAN_KIND_LABELS,
  PRICE_COMPONENT_KIND_LABELS,
  actionLabel,
  formatCount,
  plural,
  productName,
  versionLabel,
} from '../../format';
import { intelligenceLevelLabel } from '../../modules';
import { useAdminRead } from '../../useAdminRead';
import type { BillingNoticeState } from './BillingPage';
import { ContentField, contentIssues, contentText, type ContentIssue } from './ContentField';
import { UUID } from './shared';

const scaleFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 3 });

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div>
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function componentLabel(content: PlanVersionContent, key: string): string {
  return content.components.find((c) => c.key === key)?.label ?? key;
}

/** How a component prices, in the words of its kind, from the numbers platform-api sent (units changed, nothing computed). */
function ComponentPrice({ component, content }: { component: PriceComponent; content: PlanVersionContent }) {
  const currency = content.currency;
  switch (component.kind) {
    case 'flat_recurring':
      return <>{formatMoney(component.amountMicros, currency)} per period</>;
    case 'licensed_quantity':
      return (
        <>
          {formatMoney(component.unitPriceMicros, currency)} per{' '}
          {LICENSED_UNIT_LABELS[component.unit] ?? component.unit} per period
          {component.maxQuantity === undefined ? null : <>, at most {formatCount(component.maxQuantity)}</>}
        </>
      );
    case 'metered_curve':
      return (
        <ul className="admin-plain-list admin-plain-list--flush">
          <li>Start price: {formatUnitPrice(component.startPriceMicros, currency)} per token</li>
          <li>Floor price: {formatUnitPrice(component.floorPriceMicros, currency)} per token</li>
          <li>Scale: {scaleFormat.format(component.scaleTokens)} tokens</li>
        </ul>
      );
    case 'metered_graduated':
      return (
        <ul className="admin-plain-list admin-plain-list--flush">
          {component.bands.map((band, index) => (
            <li key={index}>
              {band.upToTokens === null
                ? 'Beyond the last band'
                : `Up to ${formatCount(band.upToTokens)} tokens`}
              : {formatUnitPrice(band.unitPriceMicros, currency)} per token
            </li>
          ))}
          {component.minimumMicros > 0 ? (
            <li>At least {formatMoney(component.minimumMicros, currency)} per period</li>
          ) : null}
        </ul>
      );
    case 'conditional_discount':
      return (
        <ul className="admin-plain-list admin-plain-list--flush">
          <li>
            {formatPercent(component.percentBasisPoints)} off {componentLabel(content, component.appliesTo)}
          </li>
          <li>
            While these modules are enabled:{' '}
            {component.condition.moduleKeys.map((key, index) => (
              <span key={key}>
                {index > 0 ? ', ' : null}
                <code>{key}</code>
              </span>
            ))}
          </li>
          <li>{DISCOUNT_PRORATION_LABELS[component.proration] ?? component.proration}</li>
        </ul>
      );
  }
}

function ComponentsTable({ content }: { content: PlanVersionContent }) {
  return (
    <ScrollTable caption="Price components" className="admin-table admin-table--components">
      <thead>
        <tr>
          <th scope="col">Component</th>
          <th scope="col">Kind</th>
          <th scope="col">Price</th>
        </tr>
      </thead>
      <tbody>
        {content.components.map((component) => (
          <tr key={component.key}>
            <th scope="row">
              {component.label}
              <span className="admin-cell-secondary">
                <code>{component.key}</code>
              </span>
            </th>
            <td>{PRICE_COMPONENT_KIND_LABELS[component.kind] ?? component.kind}</td>
            <td className="admin-cell-wrap">
              <ComponentPrice component={component} content={content} />
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

function EstimateCell({
  estimate,
  level,
}: {
  estimate: TokenEstimate;
  level: (typeof INTELLIGENCE_LEVELS)[number];
}) {
  const tokens = estimateAtLevel(estimate, level);
  const multiplier = estimate.levelMultipliersMilli?.[level];
  if (tokens === null || multiplier === undefined) return <span className="admin-muted">Same</span>;
  return (
    <>
      {formatTokens(tokens)}
      <span className="admin-cell-secondary">{formatMultiplier(multiplier)}x</span>
    </>
  );
}

function EstimatesTable({ content }: { content: PlanVersionContent }) {
  return (
    <ScrollTable caption="Token estimates" className="admin-table admin-table--estimates">
      <thead>
        <tr>
          <th scope="col">Action</th>
          <th scope="col">Module</th>
          <th scope="col" className="admin-num">
            Reference (Seasoned)
          </th>
          {INTELLIGENCE_LEVELS.map((level) => (
            <th scope="col" className="admin-num" key={level}>
              {intelligenceLevelLabel(level)}
            </th>
          ))}
          <th scope="col" className="admin-num">
            Included per period
          </th>
        </tr>
      </thead>
      <tbody>
        {content.tokenEstimates.map((estimate) => (
          <tr key={estimate.action}>
            <th scope="row">{actionLabel(estimate.action)}</th>
            <td>
              <code>{estimate.moduleKey}</code>
            </td>
            <td className="admin-num">{formatTokens(estimate.referenceMillitokens)}</td>
            {INTELLIGENCE_LEVELS.map((level) => (
              <td className="admin-num" key={level}>
                <EstimateCell estimate={estimate} level={level} />
              </td>
            ))}
            <td className="admin-num">
              {estimate.includedPerPeriod === undefined
                ? 'No allowance'
                : formatCount(estimate.includedPerPeriod)}
            </td>
          </tr>
        ))}
      </tbody>
    </ScrollTable>
  );
}

function basisText(content: PlanVersionContent): string {
  const basis = content.billableBasis;
  if (basis.kind === 'estimate') return 'Each action bills its published estimate at its level.';
  return `Each action bills its metered tokens, clamped between ${formatMultiplier(basis.lowerMultipleMilli)} and ${formatMultiplier(basis.upperMultipleMilli)} times its estimate.`;
}

/**
 * A plan version (/billing/plan-versions/:id): its identity, status and provenance, the price components as
 * platform-api holds them (money shown from micros, never computed), the token estimates per action and level,
 * the allowed modules, the trial and the self-serve terms, and the content digest. On a draft, staff with
 * staff.billing.manage edit the whole content as JSON (checked against the schema, platform-api's issues shown at
 * their paths) and publish it, confirming the digest of the content they reviewed and giving a reason. A
 * published version offers no change: it never changes again.
 */
export function PlanVersionPage() {
  const { versionId = '' } = useParams();
  const location = useLocation();
  const abilities = useAbilities();
  const { api } = useAdmin();
  const valid = UUID.test(versionId);
  const key = valid && abilities.readBilling ? `plan-version\u0000${versionId}` : null;
  const read = useAdminRead(key, (signal) => api.getPlanVersion(versionId, signal));
  const version = read.dataKey === key && !read.failure ? read.data : null;
  const failure = valid ? read.failure : { kind: 'not-found' as const };

  const arrival = (location.state as Partial<BillingNoticeState> | null)?.notice;
  const [notice, setNotice] = useState<string | null>(typeof arrival === 'string' ? arrival : null);
  const dialog = useDialogTarget<'edit' | 'publish'>();
  const [json, setJson] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ContentIssue[]>([]);
  const [reason, setReason] = useState('');
  const [reasonProblem, setReasonProblem] = useState<string | null>(null);
  // What a dialog was opened on: an edit saves against the revision its text started from, a publish names the
  // digest that was reviewed. A conflict re-reads the page; the dialog keeps these, so a retry is refused again
  // until the dialog is opened anew on the current draft (never a silent overwrite of someone else's edit).
  const [baseRevision, setBaseRevision] = useState(0);
  const [reviewedDigest, setReviewedDigest] = useState('');
  const rereadOnConflict = (failure: WriteFailure) => {
    if (failure.kind === 'conflict') read.retry();
  };

  const openEdit = (current: PlanVersion) => {
    setBaseRevision(current.revision);
    setJson(contentText(current.content));
    setJsonError(null);
    setIssues([]);
    setReason('');
    setReasonProblem(null);
    dialog.show('edit');
  };
  const openPublish = (current: PlanVersion) => {
    setReviewedDigest(current.contentDigest);
    setReason('');
    setReasonProblem(null);
    dialog.show('publish');
  };

  const title = version ? `${version.planName} ${versionLabel(version.version)}` : 'Plan version';
  const draft = version?.status === 'draft';
  const canChange = Boolean(version && draft && abilities.manageBilling);

  let body: ReactNode;
  if (!abilities.readBilling) {
    body = <AdminProblem failure={{ kind: 'forbidden' }} subject="billing" />;
  } else if (failure) {
    body = (
      <AdminProblem
        failure={failure}
        subject="this plan version"
        onRetry={read.retry}
        action={
          failure.kind === 'not-found' || failure.kind === 'invalid-request' ? (
            <ActionLink primary to="/billing">
              Back to billing
            </ActionLink>
          ) : undefined
        }
      />
    );
  } else if (!version) {
    body = <LoadingBlock label="Loading the plan version" />;
  } else {
    const { content } = version;
    body = (
      <>
        <dl className="admin-facts admin-facts--summary" aria-label="Plan version">
          <Fact term="Status">
            <StatusBadge status={version.status} />
          </Fact>
          <Fact term="Product">{productName(version.productKey)}</Fact>
          <Fact term="Kind">
            {PLAN_KIND_LABELS[version.planKind] ?? version.planKind}
            {version.organizationId ? (
              <>
                {' '}
                for{' '}
                <Link to={`/organizations/${version.organizationId}/billing`}>
                  <code className="admin-id">{version.organizationId}</code>
                </Link>
              </>
            ) : null}
          </Fact>
          <Fact term="Billing">
            {content.currency}, every {content.billingInterval}
          </Fact>
          <Fact term="Revision">{formatCount(version.revision)}</Fact>
          <Fact term="Created">
            {version.createdBy ?? <span className="admin-muted">System</span>},{' '}
            <DateTimeText iso={version.createdAt} />
          </Fact>
          <Fact term="Published">
            {version.publishedAt ? (
              <>
                {version.publishedBy ?? <span className="admin-muted">Unknown</span>},{' '}
                <DateTimeText iso={version.publishedAt} />
              </>
            ) : (
              <span className="admin-muted">Not yet</span>
            )}
          </Fact>
        </dl>
        <PageSection
          title="Price components"
          description={`What this version prices, in ${content.currency}. Money is shown as platform-api holds it; the browser computes no price.`}
        >
          <ComponentsTable content={content} />
        </PageSection>
        <PageSection
          title="Token estimates"
          description={`The tokens one action uses at each intelligence level (Seasoned is the reference; "Same" means the action has no levels). ${basisText(content)}`}
        >
          {content.tokenEstimates.length === 0 ? (
            <p className="admin-muted">This version prices no action.</p>
          ) : (
            <EstimatesTable content={content} />
          )}
        </PageSection>
        <PageSection
          title="Allowed modules"
          description="The commercial layer of module access: what a contract on this version entitles."
        >
          {content.allowedModules.length === 0 ? (
            <p className="admin-muted">No module.</p>
          ) : (
            <ul className="admin-inline-list" aria-label="Allowed modules">
              {content.allowedModules.map((moduleKey) => (
                <li key={moduleKey}>
                  <code>{moduleKey}</code>
                </li>
              ))}
            </ul>
          )}
        </PageSection>
        <PageSection title="Terms">
          <dl className="admin-facts" aria-label="Terms of the version">
            <Fact term="Trial">
              {content.trial
                ? `${plural(content.trial.days, 'day', 'days')} from the contract start, ${content.trial.cardRequired ? 'a card required up front' : 'no card required'}, nothing charged`
                : 'None'}
            </Fact>
            <Fact term="Self-serve">
              {content.selfServe
                ? `Up to ${plural(content.selfServe.maxShipmentsPerPeriod, 'shipment', 'shipments')} a period; past that, an Enterprise prompt, never a block`
                : 'None'}
            </Fact>
            <Fact term="Source">
              {version.source || <span className="admin-muted">No source label</span>}
            </Fact>
            <Fact term="Content digest">
              <code className="admin-digest">{version.contentDigest}</code>
            </Fact>
          </dl>
        </PageSection>
        {version.status === 'published' ? (
          <p className="admin-note admin-muted">
            Published versions never change. To change a price, draft the next version from the Billing list.
          </p>
        ) : null}
      </>
    );
  }

  let actions: ReactNode = undefined;
  if (canChange && version) {
    actions = (
      <div className="admin-row-actions">
        <button type="button" className="admin-action" onClick={() => openEdit(version)}>
          Edit the content
        </button>
        <button
          type="button"
          className="admin-action admin-action--primary"
          onClick={() => openPublish(version)}
        >
          Publish
        </button>
      </div>
    );
  }

  return (
    <AdminPage title={title}>
      <Link className="admin-back" to="/billing">
        <span aria-hidden="true">&larr;</span> Billing
      </Link>
      <PageHeading
        eyebrow={version ? 'Plan version' : null}
        title={title}
        description={
          version ? (
            <span className="admin-heading-meta">
              <code>{version.planKey}</code>
              <StatusBadge status={version.status} />
            </span>
          ) : null
        }
        actions={actions}
      />
      <SavedNotice message={notice} />
      {body}
      {version && canChange ? (
        <>
          <WriteDialog
            key={`edit-${dialog.key}`}
            open={dialog.open && dialog.target === 'edit'}
            onClose={dialog.close}
            title={`Edit ${title}`}
            description={`The whole content of the draft, at revision ${formatCount(baseRevision)}. Saving moves the revision; a draft someone else changed meanwhile is refused: open Edit again to start from it.`}
            submitLabel="Save the draft"
            onSubmit={async () => {
              const checked = contentIssues(json);
              setIssues(checked.issues);
              setJsonError(checked.issues[0]?.message ?? null);
              const reasonIssue = reasonError(reason);
              setReasonProblem(reasonIssue);
              if (!checked.content || reasonIssue) return 'invalid';
              await api.updatePlanVersionDraft(version.id, {
                content: checked.content,
                expectedRevision: baseRevision,
                reason,
              });
              return undefined;
            }}
            onSaved={() => {
              setNotice(`${title} is saved.`);
              read.retry();
            }}
            onFailed={rereadOnConflict}
          >
            <ContentField value={json} onChange={setJson} error={jsonError} issues={issues} />
            <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
          </WriteDialog>
          <WriteDialog
            key={`publish-${dialog.key}`}
            open={dialog.open && dialog.target === 'publish'}
            onClose={dialog.close}
            title={`Publish ${title}?`}
            description="From then on this content never changes, and contracts can be signed on it."
            submitLabel="Publish this version"
            onSubmit={async () => {
              const reasonIssue = reasonError(reason);
              setReasonProblem(reasonIssue);
              if (reasonIssue) return 'invalid';
              await api.publishPlanVersion(version.id, reviewedDigest, reason);
              return undefined;
            }}
            onSaved={() => {
              setNotice(`${title} is published.`);
              read.retry();
            }}
            onFailed={rereadOnConflict}
          >
            <p>
              You publish the content you reviewed, with this digest. If the draft changed meanwhile, nothing
              is published: the page shows the current draft, and you publish it after reviewing it again.
            </p>
            <p>
              <code className="admin-digest" aria-label="Content digest">
                {reviewedDigest}
              </code>
            </p>
            <ReasonField value={reason} onChange={setReason} error={reasonProblem} />
          </WriteDialog>
        </>
      ) : null}
    </AdminPage>
  );
}
